/**
 * Query Parameter Helper Functions
 * Provides reusable utilities for constructing API query parameters
 */

/**
 * Apply hierarchy filters to query parameters in a cascading manner.
 * Each level of the hierarchy (org -> reg -> fac -> bldg) depends on the previous level being set.
 * 
 * Note: If params is null/undefined, a new object will be created and returned.
 * 
 * @param {Object} params - The query parameters object to modify (can be null/undefined)
 * @param {Object} hierarchyFilters - The hierarchy filter values
 * @param {string} hierarchyFilters.org - Organization filter value
 * @param {string} hierarchyFilters.reg - Region filter value
 * @param {string} hierarchyFilters.fac - Facility filter value
 * @param {string} hierarchyFilters.bldg - Building filter value
 * @returns {Object} - The modified params object with hierarchy filters applied
 * 
 * @example
 * // With initialized params
 * const params = { page: 1, page_size: 25 };
 * applyHierarchyFilters(params, { org: 'ACME', reg: 'West', fac: '', bldg: '' });
 * // params is now { page: 1, page_size: 25, org: 'ACME', reg: 'West' }
 * 
 * @example
 * const params = applyHierarchyFilters(null, { org: 'ACME', reg: 'West', fac: '', bldg: '' });
 * // params is now { org: 'ACME', reg: 'West' }
 */
export function applyHierarchyFilters(params, hierarchyFilters) {
	// Initialize params if not provided or invalid
	if (!params || typeof params !== 'object') {
		params = {};
	}

	// Return early if hierarchyFilters is invalid
	if (!hierarchyFilters || typeof hierarchyFilters !== 'object') {
		return params;
	}

	// Apply cascading hierarchy filters
	const orgValue = hierarchyFilters.org?.trim();
	if (orgValue) {
		params.org = orgValue;

		const regValue = hierarchyFilters.reg?.trim();
		if (regValue) {
			params.reg = regValue;

			const facValue = hierarchyFilters.fac?.trim();
			if (facValue) {
				params.fac = facValue;

				const bldgValue = hierarchyFilters.bldg?.trim();
				if (bldgValue) {
					params.bldg = bldgValue;
				}
			}
		}
	}

	return params;
}
